import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { ListSkeleton } from './shared/list-skeleton'
import { FlowFrame } from './shared/flow-frame'

export function getChromeVariant(routeId: string): 'full' | 'simple' | 'bare' | 'none' {
  if (!routeId.startsWith('/_app')) return 'none'
  if (routeId.startsWith('/_app/test') || routeId.startsWith('/_app/register')) return 'simple'
  if (
    routeId.startsWith('/_app/_public') ||
    routeId.startsWith('/_app/checkout') ||
    routeId.startsWith('/_app/beta') ||
    // The member describe/survey flow (`orgs_.$orgId.users.groups…`) opts out of the orgs layout via
    // the trailing-underscore non-nesting convention, so its route id is `/_app/orgs_/…` — it renders
    // chrome-less (logo + language selector only), distinct from the orgs master-detail (`/_app/orgs/…`).
    routeId.startsWith('/_app/orgs_')
  ) {
    return 'bare'
  }
  return 'full'
}

export function LogoSkeleton() {
  return <Skeleton className="h-6 w-40 shrink-0" />
}

export function NavSkeleton() {
  return (
    <nav className="hidden flex-1 items-center gap-1 md:flex">
      <Skeleton className="h-9 w-20 rounded-lg" />
      <Skeleton className="h-9 w-20 rounded-lg" />
      <Skeleton className="h-9 w-20 rounded-lg" />
    </nav>
  )
}

// The mobile bottom bar's placeholder (app-bottom-nav.tsx). Mirrors the real bar's box metrics — p-1
// outer, per-cell py-1.5 + size-5 icon + gap-0.5 + a 10px/leading-tight label — so <main> doesn't resize
// when the roster resolves.
export function BottomNavSkeleton() {
  return (
    <nav className="flex items-stretch gap-0.5 p-1">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1.5">
          <Skeleton className="size-5 shrink-0 rounded" />
          <Skeleton className="h-3 w-10 rounded" />
        </div>
      ))}
    </nav>
  )
}

export function UserMenuSkeleton() {
  return <Skeleton className="ml-auto size-9 shrink-0 rounded-full md:ml-0" />
}

export function SectionHeaderSkeleton(props: { breadcrumb?: boolean }) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b p-3 sm:gap-4 sm:p-5">
      <div className="flex min-w-0 items-center gap-2 sm:gap-3">
        <Skeleton className="size-4 shrink-0 sm:size-5" />
        <div className="flex h-6 items-center sm:h-7">
          <Skeleton className="h-4 w-28 sm:w-40" />
        </div>
        {props.breadcrumb && (
          <>
            <Skeleton className="size-3 shrink-0 rounded-sm" />
            <div className="flex h-6 items-center sm:h-7">
              <Skeleton className="h-4 w-24 sm:w-32" />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function ListPanelBodySkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-23 shrink-0 items-center gap-2 border-b px-4">
        <Skeleton className="h-8 min-w-0 flex-1 rounded-lg" />
        <Skeleton className="h-8 w-16 shrink-0 rounded-lg" />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-auto p-3">
        <ListSkeleton />
      </div>
    </div>
  )
}

// Mirrors a SettingsSection card (settings/settings-section.tsx): an optional header (icon + title +
// description) followed by `rows` horizontal field rows. Shared by the /account pending UIs.
export function SettingsSectionSkeleton(props: { rows?: number; header?: boolean }) {
  const rows = props.rows ?? 4
  return (
    <div className="bg-card rounded-xl border">
      {props.header !== false && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Skeleton className="size-7 shrink-0 rounded-lg" />
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3.5 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <Skeleton className="h-3.5 w-28 shrink-0" />
            <Skeleton className="h-8 w-full max-w-xs rounded-md" />
          </div>
        ))}
      </div>
    </div>
  )
}

// Centered settings content column (the scrollable area inside account.tsx's <Outlet/>): a stack of
// section cards. Used as the pending UI for the /account profile route.
export function SettingsContentSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <SettingsSectionSkeleton rows={3} />
      <SettingsSectionSkeleton rows={5} />
      <SettingsSectionSkeleton rows={4} />
    </div>
  )
}

// Full /account layout pending UI (account.tsx): header + settings tabs + centered content. Mirrors
// account.tsx's own markup so the chrome doesn't shift when the real layout swaps in.
export function SettingsLayoutPending() {
  return (
    <div className="flex h-full flex-col">
      <SectionHeaderSkeleton />
      <div className="border-b">
        <nav className="flex gap-1 px-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2 px-4 py-3">
              <Skeleton className="size-4 shrink-0 rounded" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </nav>
      </div>
      <div className="scrollbar relative min-h-0 flex-1 overflow-y-scroll">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
          <SettingsContentSkeleton />
        </div>
      </div>
    </div>
  )
}

function AuthFieldSkeleton() {
  return (
    <div className="grid w-full gap-2">
      <div className="flex h-5 items-center">
        <Skeleton className="h-3.5 w-24" />
      </div>
      <Skeleton className="h-8 w-full rounded-lg" />
    </div>
  )
}

export function AuthCardSkeleton(props: {
  logo?: boolean
  social?: boolean
  terms?: boolean
  forgot?: boolean
  fields?: number
}) {
  const fields = props.fields ?? 2
  return (
    <div className="mx-auto flex max-w-120 flex-col items-center gap-6 p-6">
      {props.logo && <Skeleton className="h-7 w-32" />}
      <div className="flow-header flex flex-col items-center gap-2 text-center">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <FlowFrame contentClassName="flex flex-col gap-5">
        {props.social && (
          <>
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full rounded-lg" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
            <div className="flex items-center gap-3">
              <Skeleton className="h-px flex-1" />
              <Skeleton className="h-3 w-4" />
              <Skeleton className="h-px flex-1" />
            </div>
          </>
        )}
        <div className="flex flex-col gap-3">
          {Array.from({ length: fields }, (_, i) => (
            <AuthFieldSkeleton key={i} />
          ))}
          {props.terms && (
            <div className="my-3 flex gap-2">
              <Skeleton className="size-4 shrink-0 rounded" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          )}
          {props.forgot && <Skeleton className="h-4 w-40" />}
          <Skeleton className="h-8 w-full rounded-lg" />
        </div>
      </FlowFrame>
      <Skeleton className="h-4 w-44" />
    </div>
  )
}

export function AuthPending() {
  return <AuthCardSkeleton fields={1} />
}

// Plain centered spinner for redirect/wizard pages (checkout, the bare orgs_ member-survey flow) whose
// real content is itself a spinner or a step the chunk must load before anything meaningful can paint.
export function CenteredSpinnerPending() {
  return (
    <div className="flex h-full min-h-[40vh] w-full items-center justify-center p-4">
      <Spinner className="size-6" />
    </div>
  )
}

// Search bar + rows placeholder for the paginated API management tables (applicants/companies/jobs)
// while their route chunk loads (their data uses a non-suspense query with its own inline spinner).
export function SimpleListPending() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-10 w-full rounded-lg" />
      <div className="flex flex-col gap-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    </div>
  )
}

// Generic stacked-block placeholder for full-width content panels (API playground, dev resources)
// while their route chunk loads.
export function PanelBlockPending() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  )
}

function ReportSectionHeadingSkeleton() {
  return (
    <div className="flex w-full gap-3 py-6 @sm:items-center @sm:gap-4 @sm:py-8">
      <div className="flex min-w-0 flex-1 flex-col gap-2 @sm:gap-3">
        <div className="flex items-center gap-2 @sm:gap-3">
          <Skeleton className="size-8 shrink-0 rounded-xl @sm:size-10" />
          <Skeleton className="h-5 w-44" />
        </div>
        <div className="flex h-4 items-center @sm:h-5">
          <Skeleton className="h-3.5 w-64" />
        </div>
      </div>
    </div>
  )
}

export function ReportBodySkeleton() {
  return (
    <div
      data-testid="report-body-skeleton"
      className="@container mx-auto flex w-full max-w-7xl flex-col px-3 @sm:px-4 @md:px-6 @lg:px-8"
    >
      {/* Profile section: heading + card */}
      <ReportSectionHeadingSkeleton />
      <div className="bg-card flex flex-col gap-3 rounded-xl border p-3 @sm:gap-4 @sm:p-4 @md:gap-5 @md:p-5 @lg:p-6">
        <div className="flex items-start gap-3 @sm:gap-4">
          <Skeleton className="size-12 shrink-0 rounded-lg @sm:size-16" />
          <div className="flex min-w-0 flex-1 flex-col gap-2 @sm:gap-3">
            <div className="flex min-w-0 flex-col">
              <Skeleton className="mb-1 h-5 w-40 rounded-full" />
              <div className="flex h-6 items-center @sm:h-7 @md:h-8">
                <Skeleton className="h-5 w-52 @lg:w-64" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1.5 pb-3">
            <div className="flex flex-wrap items-center gap-2 @sm:gap-3">
              <Skeleton className="h-4 w-24 @sm:h-5" />
              <Skeleton className="h-4 w-32 @sm:h-5" />
              <Skeleton className="h-4 w-28 @sm:h-5" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-28 rounded-md" />
            <Skeleton className="h-8 w-24 rounded-md" />
            <Skeleton className="h-8 w-28 rounded-md" />
          </div>
        </div>
      </div>
      {/* Results section — the deep report (job/team/own) opens on the histogram, NOT a radar, so the
          skeleton mirrors SkillsBreakdown's 3-column grid of bar cards (skills-breakdown.tsx:141) rather
          than a lone circle that lands where the bars actually render. The columns collapse 3→2→1 with
          the same container queries as the real grid, so the fill-in never reflows. */}
      <ReportSectionHeadingSkeleton />
      <div className="grid gap-4 @xl:grid-cols-2 @2xl:grid-cols-3">
        {[0, 1, 2].map((col) => (
          <div key={col} className="bg-card flex flex-col gap-4 rounded-2xl border p-5">
            <div className="border-border/50 flex items-center justify-between border-b pb-3">
              <Skeleton className="h-3.5 w-28" />
            </div>
            <div className="flex flex-col gap-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex flex-col gap-3">
                  <div className="flex h-6 items-center justify-between gap-2">
                    <Skeleton className="h-3.5 w-24" />
                    <Skeleton className="h-3.5 w-4" />
                  </div>
                  <Skeleton className="h-3 w-full rounded-full" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function ReportPaneSkeleton() {
  return (
    <>
      <SectionHeaderSkeleton />
      <div className="@container min-h-0 flex-1 overflow-auto">
        <ReportBodySkeleton />
      </div>
    </>
  )
}

function MemberRowSkeleton(props: { titleWidth: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg px-2.5 py-2">
      <Skeleton className="size-8 shrink-0 rounded-md" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Skeleton className={`h-3.5 ${props.titleWidth}`} />
        <div className="flex gap-1">
          <Skeleton className="h-3 w-6 rounded" />
          <Skeleton className="h-3 w-6 rounded" />
          <Skeleton className="h-3 w-6 rounded" />
          <Skeleton className="h-3 w-6 rounded" />
        </div>
      </div>
    </div>
  )
}

// Pre-tag with a stable id so the render map keys by the element (not the array index): the widths
// repeat, so they can't be the key themselves.
const MEMBER_ROW_SKELETONS = ['w-2/5', 'w-1/3', 'w-1/2', 'w-2/5', 'w-1/3', 'w-1/2', 'w-2/5', 'w-1/3'].map((w, i) => ({
  id: i,
  w,
}))

export function MembersPanelSkeleton() {
  return (
    <div className="flex h-full w-full flex-col">
      <div className="border-b p-3">
        <Skeleton className="h-8 w-full rounded-lg" />
      </div>
      <div className="flex flex-col gap-2 border-b p-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-24 rounded-md" />
          <Skeleton className="h-6 w-20 rounded-md" />
          <Skeleton className="h-6 w-24 rounded-md" />
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-auto p-3">
        <div className="px-2 py-1.25">
          <Skeleton className="h-2.5 w-16" />
        </div>
        {MEMBER_ROW_SKELETONS.map(({ id, w }) => (
          <MemberRowSkeleton key={id} titleWidth={w} />
        ))}
      </div>
    </div>
  )
}

// Mirrors the real ReportTabs markup (report-tabs.tsx → animated-tabs.tsx) so the placeholder lands
// exactly where the tabs render: same nav box (mx-4.5/gap-4/py-3.5), same per-tab Link box
// (h-12/gap-2/px-4) with a size-4 icon + label. Widths approximate the guaranteed Report/Distribution/
// Board labels; the label is `hidden sm:block` to match the real `hidden sm:inline` text (icon-only on mobile).
const REPORT_TAB_LABEL_WIDTHS = ['w-14', 'w-20', 'w-12']

export function ReportTabsSkeleton() {
  return (
    <div className="flex flex-col print:hidden">
      <div className="flex gap-1 border-b">
        <nav className="mx-4.5 flex shrink-0 items-center justify-center gap-4 py-3.5">
          {REPORT_TAB_LABEL_WIDTHS.map((w) => (
            <div key={w} className="flex h-7 items-center">
              <div className="flex h-12 items-center gap-2 px-4">
                <Skeleton className="size-4 shrink-0 rounded" />
                <Skeleton className={`hidden h-4 rounded sm:block ${w}`} />
              </div>
            </div>
          ))}
        </nav>
      </div>
    </div>
  )
}

export function ReportPanelSkeleton() {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <ReportTabsSkeleton />
      <div className="min-h-0 flex-1 overflow-auto">
        <ReportBodySkeleton />
      </div>
    </div>
  )
}
